/**
 * Query keys and read hooks.
 *
 * One hook per port method. Components never call the adapter directly; they
 * call a hook, which means:
 *
 *   - every screen already renders pending and error states, so the API switch
 *     is invisible to them;
 *   - reference data (the catalogue, the rule library, the SR configuration) is
 *     fetched once and shared, rather than re-read on every navigation.
 *
 * Reference data is marked `staleTime: Infinity` because it does not change
 * within a session. Mutable collections use the default so a save is reflected.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { data } from './index';
import { buildHumanLayerManifest, type HumanLayerManifest } from '@/domain/humanLayer/manifest';
import type {
  VendorAssessmentSnapshot,
  VendorOnboardingRequestDraft,
  VendorOnboardingRequestReceipt,
  VendorRegisterHit,
  BetaRegistration,
  HumanLayerProfileDraft,
  HumanLayerVisionProfileDraft,
  HumanLayerVoiceProfileDraft,
  Id,
  MmmActivity,
  MmmActivityQuery,
  MmmActivityYearCount,
  MmmContractEntitlements,
  MmmDqsAgent,
  MmmDqsResult,
  MmmRateCard,
  MmmRateCardRevision,
  MmmReport,
  MmmScopeQuery,
  MmmSpendBasis,
  MmmSpendFile,
  MmmSpendTemplate,
  MmmSpendUpload,
  PinDraft,
  ServiceScope,
  StoredDocument,
  StoredDocumentMeta,
} from './contracts';
import { buildStoredDocument, type DocumentDraft } from './documents/createDocument';

/** Hierarchical keys, so a feature can invalidate its own subtree. */
export const qk = {
  catalogue: ['catalogue'] as const,
  modules: ['catalogue', 'modules'] as const,
  zones: ['catalogue', 'zones'] as const,
  account: ['account'] as const,
  serviceQuote: ['service-pricing', 'quote'] as const,
  sessionUser: ['catalogue', 'session-user'] as const,

  betaRegistrations: ['beta', 'registrations'] as const,

  pins: ['pins'] as const,

  analyses: ['library', 'analyses'] as const,

  external: ['research', 'external'] as const,
  researchVocab: ['research', 'vocabulary'] as const,

  documents: ['documents', 'list'] as const,
  documentBytes: ['documents', 'bytes-used'] as const,

  channels: ['measurement', 'channels'] as const,
  priceBook: ['measurement', 'price-book'] as const,

  qaFamilies: ['data-quality', 'families'] as const,
  qaRules: ['data-quality', 'rules'] as const,
  dqsDatasets: ['data-quality', 'datasets'] as const,
  dqsScopes: ['data-quality', 'scopes'] as const,
  dqsAgents: ['data-quality', 'agents'] as const,
  datasetShape: (id: Id) => ['data-quality', 'shape', id] as const,
  defectProfile: (id: Id) => ['data-quality', 'defects', id] as const,

  mmmPartnerships: ['mmm', 'partnerships'] as const,
  mmmChannels: ['mmm', 'channels'] as const,
  mmmCostModel: ['mmm', 'cost-model'] as const,
  mmmSources: ['mmm', 'sources'] as const,
  mmmMetrics: ['mmm', 'metrics'] as const,
  mmmOutputs: ['mmm', 'outputs'] as const,
  mmmOutput: (id: Id) => ['mmm', 'outputs', id] as const,

  mmmContractRecords: ['mmm', 'variable-creation', 'contracts'] as const,
  mmmCountries: ['mmm', 'variable-creation', 'countries'] as const,
  mmmGeoChildren: (parentId: Id) => ['mmm', 'variable-creation', 'geo', parentId] as const,
  mmmGeoNodes: (ids: readonly Id[]) =>
    ['mmm', 'variable-creation', 'geo-nodes', [...ids].sort().join('|')] as const,
  mmmVisibilityChannels: ['mmm', 'variable-creation', 'channels'] as const,
  mmmMetricDefinitions: ['mmm', 'variable-creation', 'metrics'] as const,
  mmmEntitlementDefinitions: ['mmm', 'variable-creation', 'entitlements'] as const,
  mmmRateCard: ['mmm', 'variable-creation', 'rate-card'] as const,

  srConfig: ['strategic-relevance', 'configuration'] as const,
  srPartners: ['strategic-relevance', 'partners'] as const,
  srContracts: ['strategic-relevance', 'contracts'] as const,

  pricingReference: ['pricing', 'reference'] as const,

  activationReports: ['activation', 'reports'] as const,
  activationSections: ['activation', 'sections'] as const,
  activationDeepDives: ['activation', 'deep-dives'] as const,
  activationDatasets: ['activation', 'datasets'] as const,
  activationVocab: ['activation', 'vocabulary'] as const,
  activationDemo: (id: Id) => ['activation', 'demo', id] as const,

  humanLayerProfile: ['human-layer', 'profile'] as const,
  humanLayerVoice: ['human-layer', 'voice'] as const,
  humanLayerVision: ['human-layer', 'vision'] as const,
};

/** Reference data does not change within a session. */
const REFERENCE = { staleTime: Infinity, gcTime: Infinity } as const;

/* ------------------------------------------------------------- catalogue -- */

export const useModules = () =>
  useQuery({ queryKey: qk.modules, queryFn: () => data.catalogue.listModules(), ...REFERENCE });

export const useZones = () =>
  useQuery({ queryKey: qk.zones, queryFn: () => data.catalogue.listZones(), ...REFERENCE });

export const useAccount = () =>
  useQuery({ queryKey: qk.account, queryFn: () => data.catalogue.getAccount(), ...REFERENCE });

/**
 * A quote for a scope, from the Partnership Net Pricing Engine (DEC-48).
 *
 * Deliberately NOT `REFERENCE`-cached: a rate card is the one thing in this
 * application that can change under you between two screens, and a price cached
 * for the session is a price that goes stale silently. The key carries the whole
 * scope, so changing the scope re-quotes rather than showing the old number
 * against the new configuration — which is the failure mode worth paying a
 * request to avoid.
 *
 * `enabled` is false for an empty scope: quoting nothing produces a zero, and a
 * zero on a pricing panel reads as "free" rather than "not configured yet".
 */
export const useServiceQuote = (scope: ServiceScope | null) =>
  useQuery({
    queryKey: [...qk.serviceQuote, scope],
    queryFn: () => {
      if (scope === null) throw new Error('useServiceQuote: no scope');
      return data.servicePricing.quote(scope);
    },
    enabled: scope !== null && scope.lines.length > 0,
    staleTime: 0,
  });

export const useSessionUser = () =>
  useQuery({
    queryKey: qk.sessionUser,
    queryFn: () => data.catalogue.getSessionUser(),
    ...REFERENCE,
  });

/* --------------------------------------------------------------- library -- */

/**
 * Beta demand.
 *
 * Not reference data: a registration arrives while someone is looking at the
 * screen, so the default staleness applies and a save is reflected.
 */
export const useBetaRegistrations = () =>
  useQuery({ queryKey: qk.betaRegistrations, queryFn: () => data.beta.listRegistrations() });

export function useRegisterForBeta() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (registration: BetaRegistration) => data.beta.register(registration),
    onSuccess: () => client.invalidateQueries({ queryKey: qk.betaRegistrations }),
  });
}

/* ------------------------------------------------------------------ pins -- */

/**
 * Pinned shortcuts on the shell rail.
 *
 * Not reference data: pinning happens while someone is looking at the rail,
 * so the default staleness applies and a write is reflected right away.
 */
export const usePins = () => useQuery({ queryKey: qk.pins, queryFn: () => data.pins.listPins() });

export function usePinMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: PinDraft) => data.pins.pin(draft),
    onSuccess: () => client.invalidateQueries({ queryKey: qk.pins }),
  });
}

export function useUnpinMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => data.pins.unpin(id),
    onSuccess: () => client.invalidateQueries({ queryKey: qk.pins }),
  });
}

/**
 * Reorder returns the full, authoritative set, so the cache is replaced
 * directly rather than invalidated — the same pattern `useWriteMmmRateCard`
 * uses and for the same reason: one round trip, not a write followed by a
 * refetch.
 */
export function useReorderPinsMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: readonly Id[]) => data.pins.reorder(orderedIds),
    onSuccess: (pins) => client.setQueryData(qk.pins, pins),
  });
}

export const useSeedAnalyses = () =>
  useQuery({ queryKey: qk.analyses, queryFn: () => data.library.listAnalyses(), ...REFERENCE });

/* -------------------------------------------------------------- research -- */

export const useSeedExternalReports = () =>
  useQuery({
    queryKey: qk.external,
    queryFn: () => data.research.listExternalReports(),
    ...REFERENCE,
  });

export const useResearchVocabulary = () =>
  useQuery({
    queryKey: qk.researchVocab,
    queryFn: () => data.research.getVocabulary(),
    ...REFERENCE,
  });

/* ------------------------------------------------------------- documents -- */

/**
 * Stored documents are NOT reference data — an upload changes them — so they
 * use the default staleness and every mutation invalidates the list.
 */
export const useDocuments = () =>
  useQuery({ queryKey: qk.documents, queryFn: () => data.documents.listDocuments() });

export const useDocumentBytesUsed = () =>
  useQuery({ queryKey: qk.documentBytes, queryFn: () => data.documents.usedBytes() });

/** Whether this browser can store documents at all. Synchronous, so no query. */
export const documentStorageAvailable = (): boolean => data.documents.isAvailable();

/**
 * One document including its bytes, fetched on demand.
 *
 * Imperative rather than a hook because the only caller is a click handler
 * (download, or handing the file to an agent). Making it a query would cache
 * blobs no one is looking at.
 */
export const fetchStoredDocument = (id: Id): Promise<StoredDocument | undefined> =>
  data.documents.getDocument(id);

/**
 * Save a picked file. Returns the stored metadata.
 *
 * The auto-save is the mutation itself: the file lands in IndexedDB before the
 * dialog closes, so nothing depends on the user completing a later step.
 */
export function useUploadDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (draft: DocumentDraft): Promise<StoredDocumentMeta> =>
      data.documents.putDocument(buildStoredDocument(draft)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.documents });
      void client.invalidateQueries({ queryKey: qk.documentBytes });
    },
  });
}

export function useDeleteDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id): Promise<void> => data.documents.deleteDocument(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.documents });
      void client.invalidateQueries({ queryKey: qk.documentBytes });
    },
  });
}

/* ----------------------------------------------------------- measurement -- */

export const useChannels = () =>
  useQuery({
    queryKey: qk.channels,
    queryFn: () => data.measurement.listChannels(),
    ...REFERENCE,
  });

export const usePriceBook = () =>
  useQuery({
    queryKey: qk.priceBook,
    queryFn: () => data.measurement.listPriceBook(),
    ...REFERENCE,
  });

/* ---------------------------------------------------------- data quality -- */

export const useQaFamilies = () =>
  useQuery({
    queryKey: qk.qaFamilies,
    queryFn: () => data.dataQuality.listFamilies(),
    ...REFERENCE,
  });

export const useQaRules = () =>
  useQuery({ queryKey: qk.qaRules, queryFn: () => data.dataQuality.listRules(), ...REFERENCE });

export const useDqsDatasets = () =>
  useQuery({
    queryKey: qk.dqsDatasets,
    queryFn: () => data.dataQuality.listDatasets(),
    ...REFERENCE,
  });

export const useDqsScopes = () =>
  useQuery({
    queryKey: qk.dqsScopes,
    queryFn: () => data.dataQuality.listScopes(),
    ...REFERENCE,
  });

export const useDqsAgentSummaries = () =>
  useQuery({
    queryKey: qk.dqsAgents,
    queryFn: () => data.dataQuality.listAgentSummaries(),
    ...REFERENCE,
  });

/**
 * `enabled` already guarantees a non-null id, but the type system cannot see
 * that, so the query function states the invariant instead of asserting it away.
 */
const requireId = (id: Id | null, what: string): Id => {
  if (id === null) throw new Error(`${what} was queried without an id.`);
  return id;
};

export const useDatasetShape = (id: Id | null) =>
  useQuery({
    queryKey: qk.datasetShape(id ?? 'none'),
    queryFn: () => data.dataQuality.getDatasetShape(requireId(id, 'Dataset shape')),
    enabled: id !== null,
    ...REFERENCE,
  });

export const useDefectProfile = (id: Id | null) =>
  useQuery({
    queryKey: qk.defectProfile(id ?? 'none'),
    queryFn: () => data.dataQuality.getDefectProfile(requireId(id, 'Defect profile')),
    enabled: id !== null,
    ...REFERENCE,
  });

/* -------------------------------------------------------------------- MMM -- */

export const useMmmPartnerships = () =>
  useQuery({
    queryKey: qk.mmmPartnerships,
    queryFn: () => data.mmm.listPartnerships(),
    ...REFERENCE,
  });

export const useMmmChannelConfig = () =>
  useQuery({
    queryKey: qk.mmmChannels,
    queryFn: () => data.mmm.listChannelConfig(),
    ...REFERENCE,
  });

export const useMmmCostModel = () =>
  useQuery({ queryKey: qk.mmmCostModel, queryFn: () => data.mmm.getCostModel(), ...REFERENCE });

export const useMmmSources = () =>
  useQuery({ queryKey: qk.mmmSources, queryFn: () => data.mmm.listSources(), ...REFERENCE });

export const useMmmMetrics = () =>
  useQuery({ queryKey: qk.mmmMetrics, queryFn: () => data.mmm.listMetrics(), ...REFERENCE });

export const useMmmOutput = (id: Id) =>
  useQuery({ queryKey: qk.mmmOutput(id), queryFn: () => data.mmm.getOutput(id), ...REFERENCE });

/* ------------------------------------------------ MMM variable creation ----- */

/** ACCESS-MANAGED on the server. The screen never narrows this list for permission. */
export const useMmmContractRecords = () =>
  useQuery({
    queryKey: qk.mmmContractRecords,
    queryFn: () => data.mmm.listContractRecords(),
    ...REFERENCE,
  });

export const useMmmCountries = () =>
  useQuery({ queryKey: qk.mmmCountries, queryFn: () => data.mmm.listCountries(), ...REFERENCE });

/** One level of the geography tree. Walked on demand — the whole tree is not a payload. */
export const useMmmGeoChildren = (parentId: Id | null) =>
  useQuery({
    queryKey: qk.mmmGeoChildren(parentId ?? 'none'),
    queryFn: () => data.mmm.listGeoChildren(requireId(parentId, 'Geography children')),
    enabled: parentId !== null,
    ...REFERENCE,
  });

/**
 * Several levels at once — the DMAs under every region of a country, say. One query per
 * parent, so a cached level is never re-read and the list can be any length.
 */
export const useMmmGeoChildrenMany = (parentIds: readonly Id[]) =>
  useQueries({
    queries: parentIds.map((parentId) => ({
      queryKey: qk.mmmGeoChildren(parentId),
      queryFn: () => data.mmm.listGeoChildren(parentId),
      ...REFERENCE,
    })),
    combine: (results) => ({
      nodes: results.flatMap((r) => r.data ?? []),
      pending: results.some((r) => r.isPending),
    }),
  });

/** Labels for a saved selection of ids. Read, never stored (DEC-08). */
export const useMmmGeoNodes = (ids: readonly Id[]) =>
  useQuery({
    queryKey: qk.mmmGeoNodes(ids),
    queryFn: () => data.mmm.readGeoNodes(ids),
    enabled: ids.length > 0,
    ...REFERENCE,
  });

export const useMmmVisibilityChannels = () =>
  useQuery({
    queryKey: qk.mmmVisibilityChannels,
    queryFn: () => data.mmm.listVisibilityChannels(),
    ...REFERENCE,
  });

export const useMmmMetricDefinitions = () =>
  useQuery({
    queryKey: qk.mmmMetricDefinitions,
    queryFn: () => data.mmm.listMetricDefinitions(),
    ...REFERENCE,
  });

export const useMmmEntitlementDefinitions = () =>
  useQuery({
    queryKey: qk.mmmEntitlementDefinitions,
    queryFn: () => data.mmm.listEntitlementDefinitions(),
    ...REFERENCE,
  });

/** The card WITH its version. Not reference data: an administrator can change it. */
export const useMmmRateCard = () =>
  useQuery({ queryKey: qk.mmmRateCard, queryFn: () => data.mmm.readRateCard() });

/**
 * The reads a STEP triggers rather than a screen displays: pulls, uploads, lookups.
 * Imperative, because each is the consequence of a user action with a visible run, not
 * something to cache behind a hook. Still routed through the port, never an adapter.
 */
export const readMmmEntitlements = (
  contractIds: readonly Id[],
): Promise<readonly MmmContractEntitlements[]> => data.mmm.readEntitlements(contractIds);

export const countMmmActivitiesByYear = (
  query: MmmScopeQuery,
): Promise<readonly MmmActivityYearCount[]> => data.mmm.countActivitiesByYear(query);

export const listMmmActivities = (query: MmmActivityQuery): Promise<readonly MmmActivity[]> =>
  data.mmm.listActivities(query);

export const listMmmReports = (query: MmmScopeQuery): Promise<readonly MmmReport[]> =>
  data.mmm.listReports(query);

export const readMmmSpendTemplate = (): Promise<MmmSpendTemplate> => data.mmm.readSpendTemplate();

export const parseMmmSpendUpload = (
  file: MmmSpendFile,
  basis: MmmSpendBasis,
): Promise<MmmSpendUpload> => data.mmm.parseSpendUpload(file, basis);

/** Joined on the run identifier alone (NEW-28). */
export const findMmmDqsResults = (
  runId: Id,
  agentsInScope: readonly MmmDqsAgent[],
): Promise<readonly MmmDqsResult[]> => data.mmm.findDqsResults(runId, agentsInScope);

/** Administrator write. A stale version is refused by the port, not by the screen (DEC-09). */
export function useWriteMmmRateCard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      card,
      version,
    }: {
      readonly card: MmmRateCard;
      readonly version: string;
    }): Promise<MmmRateCardRevision> => data.mmm.writeRateCard(card, version),
    onSuccess: (revision) => {
      client.setQueryData(qk.mmmRateCard, revision);
    },
  });
}

/* --------------------------------------------------- strategic relevance -- */

export const useSrConfiguration = () =>
  useQuery({
    queryKey: qk.srConfig,
    queryFn: () => data.strategicRelevance.getConfiguration(),
    ...REFERENCE,
  });

export const useSrPartners = () =>
  useQuery({
    queryKey: qk.srPartners,
    queryFn: () => data.strategicRelevance.listPartners(),
    ...REFERENCE,
  });

export const useSrContracts = () =>
  useQuery({
    queryKey: qk.srContracts,
    queryFn: () => data.strategicRelevance.listContracts(),
    ...REFERENCE,
  });

/* --------------------------------------------------------------- pricing -- */

export const usePricingReference = () =>
  useQuery({
    queryKey: qk.pricingReference,
    queryFn: () => data.pricing.getReference(),
    ...REFERENCE,
  });

/* ------------------------------------------------------------ activation -- */

export const useSeedActivationReports = () =>
  useQuery({
    queryKey: qk.activationReports,
    queryFn: () => data.activation.listReports(),
    ...REFERENCE,
  });

export const useActivationSections = () =>
  useQuery({
    queryKey: qk.activationSections,
    queryFn: () => data.activation.listSections(),
    ...REFERENCE,
  });

export const useActivationDeepDives = () =>
  useQuery({
    queryKey: qk.activationDeepDives,
    queryFn: () => data.activation.listDeepDives(),
    ...REFERENCE,
  });

export const useActivationDatasets = () =>
  useQuery({
    queryKey: qk.activationDatasets,
    queryFn: () => data.activation.listDatasets(),
    ...REFERENCE,
  });

export const useActivationVocabulary = () =>
  useQuery({
    queryKey: qk.activationVocab,
    queryFn: () => data.activation.getVocabulary(),
    ...REFERENCE,
  });

export const useActivationDemo = (id: Id) =>
  useQuery({
    queryKey: qk.activationDemo(id),
    queryFn: () => data.activation.getDemo(id),
    ...REFERENCE,
  });

/* ---------------------------------------------- human layer: my profile -- */

/**
 * The signed-in user's Human Layer profile (Settings → Human Layer → My
 * Profile). Not reference data: a save should be reflected the moment it is
 * made, so this uses the default staleness rather than `REFERENCE`.
 */
export const useHumanLayerProfile = () =>
  useQuery({
    queryKey: qk.humanLayerProfile,
    queryFn: () => data.humanLayerProfile.getProfile(),
  });

/**
 * `updateProfile` returns the full, authoritative record, so the cache is
 * replaced directly rather than invalidated — the same pattern
 * `useReorderPinsMutation` and `useWriteMmmRateCard` use above, and for the
 * same reason: one round trip, not a write followed by a refetch against a
 * static adapter that would just hand back the pre-save seed again.
 */
export function useUpdateHumanLayerProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: HumanLayerProfileDraft) => data.humanLayerProfile.updateProfile(draft),
    onSuccess: (profile) => client.setQueryData(qk.humanLayerProfile, profile),
  });
}

/**
 * The signed-in user's Human Layer voice profile (Settings → Human Layer →
 * My Voice). Not reference data, same reasoning as `useHumanLayerProfile`
 * above: a save should be reflected the moment it is made.
 */
export const useHumanLayerVoiceProfile = () =>
  useQuery({
    queryKey: qk.humanLayerVoice,
    queryFn: () => data.humanLayerVoice.getVoiceProfile(),
  });

/**
 * `updateVoiceProfile` returns the full, authoritative record, so the cache is
 * replaced directly rather than invalidated — same pattern as
 * `useUpdateHumanLayerProfile` above, for the same reason.
 */
export function useUpdateHumanLayerVoiceProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: HumanLayerVoiceProfileDraft) =>
      data.humanLayerVoice.updateVoiceProfile(draft),
    onSuccess: (voice) => client.setQueryData(qk.humanLayerVoice, voice),
  });
}

/**
 * The signed-in workspace's Human Layer vision profile (Settings → Human
 * Layer → My Vision). Not reference data, same reasoning as
 * `useHumanLayerVoiceProfile` above: a save should be reflected the moment
 * it is made.
 */
export const useHumanLayerVisionProfile = () =>
  useQuery({
    queryKey: qk.humanLayerVision,
    queryFn: () => data.humanLayerVision.getVisionProfile(),
  });

/**
 * `updateVisionProfile` returns the full, authoritative record, so the cache
 * is replaced directly rather than invalidated — same pattern as
 * `useUpdateHumanLayerVoiceProfile` above, for the same reason.
 */
export function useUpdateHumanLayerVisionProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: HumanLayerVisionProfileDraft) =>
      data.humanLayerVision.updateVisionProfile(draft),
    onSuccess: (vision) => client.setQueryData(qk.humanLayerVision, vision),
  });
}

/**
 * The manifest every prompt-building call site is meant to read instead of
 * `data.humanLayerProfile` / `data.humanLayerVoice` / `data.humanLayerVision`
 * directly — see `@/domain/humanLayer/manifest`'s docblock for the full
 * reasoning. Imperative like `fetchStoredDocument` above, not a hook: its
 * callers have no component to subscribe from. A screen that already holds a
 * profile, a voice profile and a vision profile from their own hooks should
 * call `buildHumanLayerManifest()` directly rather than fetch second copies
 * through this.
 */
export const getHumanLayerManifest = (): Promise<HumanLayerManifest> =>
  Promise.all([
    data.humanLayerProfile.getProfile(),
    data.humanLayerVoice.getVoiceProfile(),
    data.humanLayerVision.getVisionProfile(),
  ]).then(([profile, voice, vision]) => buildHumanLayerManifest(profile, voice, vision));

/** Re-exported so features can type a hook result without importing the lib. */
export type { UseQueryResult };

/* ------------------------------------------------- marketplace vendor register -- */

/**
 * The register is read, never written to from here, with one exception: the
 * onboarding request, which is a deliberate human act and so is a mutation.
 *
 * No `staleTime: Infinity`. An assessment expires, and a screen holding a
 * cached "approved" past its validity would show a vendor as usable when it is
 * not — which is the one thing the listing gate exists to stop.
 */
export function useVendorRegisterSearch(
  query: string,
  filters?: Parameters<typeof data.vendorRegister.search>[1],
): UseQueryResult<readonly VendorRegisterHit[]> {
  return useQuery({
    queryKey: ['vendorRegister', 'search', query, filters ?? null],
    queryFn: () => data.vendorRegister.search(query, filters),
    // A blank query would ask the server for the whole register.
    enabled: query.trim().length > 0,
  });
}

export function useVendorAssessment(
  solutionId: Id | null,
): UseQueryResult<VendorAssessmentSnapshot | null> {
  return useQuery({
    queryKey: ['vendorRegister', 'assessment', solutionId],
    queryFn: () => data.vendorRegister.getAssessment(requireId(solutionId, 'Vendor assessment')),
    enabled: solutionId !== null,
  });
}

export function useOnboardingRequest(
  requestId: Id | null,
): UseQueryResult<VendorOnboardingRequestReceipt> {
  return useQuery({
    queryKey: ['vendorRegister', 'onboardingRequest', requestId],
    queryFn: () =>
      data.vendorRegister.getOnboardingRequest(requireId(requestId, 'Onboarding request')),
    enabled: requestId !== null,
  });
}

export function useCreateOnboardingRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: VendorOnboardingRequestDraft) =>
      data.vendorRegister.createOnboardingRequest(draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vendorRegister'] });
    },
  });
}
